import { Tooltip } from "@gotts.ai/ui";

import { PcbCard } from "../../components/PcbCard";
import { DEVENV_TOKEN_PRICES, TOKEN_DECIMALS } from "../../lib/constants";
import { formatUsd } from "../../lib/format";

import { resolveSymbol } from "./types";

import type { V2Position, V3Position, V4Position } from "./types";
import type { Address } from "viem";

interface PositionSummaryCardProps {
  v2Positions: V2Position[];
  v3Positions: V3Position[];
  v4Positions: V4Position[];
  poolTicks: Record<string, number>;
  tokens: Record<string, Address>;
  loading?: boolean;
}

export function PositionSummaryCard({
  v2Positions,
  v3Positions,
  v4Positions,
  poolTicks,
  tokens,
  loading,
}: PositionSummaryCardProps) {
  const totalPositions =
    v2Positions.length + v3Positions.length + v4Positions.length;

  // Estimate total value from V2 user shares
  let totalValueUsd = 0;
  for (const pos of v2Positions) {
    const sym0 = resolveSymbol(pos.token0, tokens);
    const sym1 = resolveSymbol(pos.token1, tokens);
    const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
    const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
    const val0 =
      (Number(pos.userShare0) / 10 ** dec0) * (DEVENV_TOKEN_PRICES[sym0] ?? 0);
    const val1 =
      (Number(pos.userShare1) / 10 ** dec1) * (DEVENV_TOKEN_PRICES[sym1] ?? 0);
    totalValueUsd += val0 + val1;
  }

  // V3 fee value estimate
  let uncollectedFeesUsd = 0;
  let inRangeCount = 0;
  let outOfRangeCount = 0;

  for (const pos of v3Positions) {
    const sym0 = resolveSymbol(pos.token0, tokens);
    const sym1 = resolveSymbol(pos.token1, tokens);
    const dec0 = TOKEN_DECIMALS[sym0] ?? 18;
    const dec1 = TOKEN_DECIMALS[sym1] ?? 18;
    const feeVal0 =
      (Number(pos.tokensOwed0) / 10 ** dec0) * (DEVENV_TOKEN_PRICES[sym0] ?? 0);
    const feeVal1 =
      (Number(pos.tokensOwed1) / 10 ** dec1) * (DEVENV_TOKEN_PRICES[sym1] ?? 0);
    uncollectedFeesUsd += feeVal0 + feeVal1;

    const poolKey = `${pos.token0}:${pos.token1}:${String(pos.fee)}`;
    const currentTick = poolTicks[poolKey];
    if (currentTick !== undefined) {
      if (pos.tickLower <= currentTick && currentTick < pos.tickUpper) {
        inRangeCount++;
      } else {
        outOfRangeCount++;
      }
    }
  }

  const hasConcentrated = v3Positions.length + v4Positions.length > 0;
  const isV2Only =
    v2Positions.length > 0 &&
    v3Positions.length === 0 &&
    v4Positions.length === 0;

  if (loading) {
    return (
      <PcbCard label="SUM-01" title="Position Summary">
        <div className="space-y-4" data-testid="position-summary">
          <div className="skeleton skeleton-hero" />
          <div className="grid grid-cols-3 gap-4">
            <div className="skeleton skeleton-row" />
            <div className="skeleton skeleton-row" />
            <div className="skeleton skeleton-row" />
          </div>
        </div>
      </PcbCard>
    );
  }

  return (
    <PcbCard
      label="SUM-01"
      title="Position Summary"
      className="summary-card-accent"
    >
      <div data-testid="position-summary" className="space-y-4">
        {/* Hero value */}
        <div>
          <span className="hero-value-label mb-1 block">
            Total Estimated Value
          </span>
          <span className="hero-value">
            {totalValueUsd > 0 ? formatUsd(totalValueUsd) : "$0.00"}
          </span>
          {totalValueUsd === 0 && totalPositions === 0 && (
            <p className="type-supporting mt-1">
              Add liquidity to see your portfolio value
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-[var(--glass-border)]" />

        {/* Secondary stats */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <span className="stat-label block">Total Positions</span>
            <div className="mt-1 flex items-center gap-2">
              <span className="stat-value">{totalPositions}</span>
              {v2Positions.length > 0 && (
                <span className="protocol-pill protocol-pill-v2">
                  V2: {v2Positions.length}
                </span>
              )}
              {v3Positions.length > 0 && (
                <span className="protocol-pill protocol-pill-v3">
                  V3: {v3Positions.length}
                </span>
              )}
              {v4Positions.length > 0 && (
                <span className="protocol-pill protocol-pill-v4">
                  V4: {v4Positions.length}
                </span>
              )}
            </div>
          </div>
          <div>
            <span className="stat-label block">Uncollected Fees</span>
            <span
              className={`stat-value mt-1 block ${uncollectedFeesUsd === 0 ? "opacity-60" : ""}`}
            >
              <Tooltip content="V2 fees are auto-compounded into LP tokens">
                <span className="cursor-help">
                  {uncollectedFeesUsd > 0
                    ? formatUsd(uncollectedFeesUsd)
                    : "$0.00"}
                </span>
              </Tooltip>
            </span>
          </div>
          <div>
            <span className="stat-label block">Range Status</span>
            <div className="mt-1">
              {isV2Only ? (
                <span className="badge-position-type">All Full Range</span>
              ) : !hasConcentrated ? (
                <span className="stat-value">--</span>
              ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                  {inRangeCount > 0 && (
                    <span className="badge-in-range">
                      {inRangeCount} in range
                    </span>
                  )}
                  {outOfRangeCount > 0 && (
                    <span className="badge-out-of-range">
                      {outOfRangeCount} out
                    </span>
                  )}
                  {inRangeCount === 0 && outOfRangeCount === 0 && (
                    <span className="stat-value">--</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PcbCard>
  );
}
